import { apiClient } from "@/api/apiClient";
import { isLasPointCloudFile } from "@/components/AddData/uploadTypeResolver";
import store from "@/store/index";
import { toApiDistanceUnit } from "@/utils/assetImportDistanceUnit";

/**
 * Import a las / laz / txt / csv point cloud from an EB Platform node (v2-server).
 * Creates the point cloud row and starts processing in the background; the
 * result is tracked through `processing_status` on the point cloud list.
 * @param {string} siteId - The site ID
 * @param {Object} body - Import parameters
 * @param {string} body.name - Name of the point cloud
 * @param {string} body.date - Date of the point cloud (YYYY-MM-DD)
 * @param {string} body.time - Time of the point cloud (HH:mm:ss)
 * @param {string} body.file_name - Source file name; the extension picks las (las / laz) vs delimited text
 * @param {string} [body.axis] - Column layout of txt/csv input (ENZ, NEZRGB, ...); omit for las / laz
 * @param {string} [body.distance_unit] - Unit selected in the upload dialog; site unit when omitted
 * @param {string} body.bucket_id - Bucket ID
 * @param {string} body.node_id - Node ID
 * @returns {Promise<{point_cloud_id: number}>}
 */
export const importPointCloud = async (
	siteId,
	{ name, date, time, file_name, axis, distance_unit, bucket_id, node_id },
) => {
	const res = await apiClient.v2.api[":siteId"]["point-cloud"].$post({
		param: { siteId },
		json: {
			name,
			date,
			time,
			source: { type: "ebPlatformNode", bucketId: bucket_id, nodeId: node_id },
			input: isLasPointCloudFile(file_name)
				? { type: "las" }
				: { type: "delimitedText", axis: axis ?? "ENZ" },
			distanceUnit: toApiDistanceUnit(
				distance_unit ?? store.state.site?.units?.distance,
			),
		},
	});
	if (!res.ok) {
		throw new Error(`Failed to import point cloud: ${res.status}`);
	}
	const { id } = await res.json();
	return { point_cloud_id: id };
};
